import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { findIndex, clamp, isEqual, noop } from 'lodash';

import { LayoutListener } from '@dbk/ui-responsive';
import { View, isNative } from '@dbk/ui-react-utils';

import DonutChartSvg from './donut-chart-svg';
import createChartData from './donut-chart-data';
import createChartInteraction from './donut-chart-interaction.native';
import DonutChartInner from './donut-chart-inner';
import { TOUCHABLE_AREA_PERCENT_INNER, TAU } from './donut-chart-constants';

export default (styles = {}) =>
  class DonutChart extends Component {

    static propTypes = {
      onLayout: PropTypes.func,
      onSelectedItem: PropTypes.func,
      DonutChartPreloader: PropTypes.func.isRequired,
      renderSelectedItem: PropTypes.func,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          color: PropTypes.string,
        })
      ).isRequired,
      loading: PropTypes.bool,
      isTablet: PropTypes.bool,
      placeholderItem: PropTypes.object || PropTypes.node,
    };

    static defaultProps = {
      onLayout: noop,
      onSelectedItem: noop,
      interactive: true,
      renderSelectedItem: () => null,
      onIsInteracting: () => null,
      loading: undefined,
      isTablet: false,
      placeholderItem: undefined,
    };

    veryWideScreen = false;

    constructor(props) {
      super(props);
      this.state = {
        items: createChartData(props.items),
        selectedIndex: this.indexForSelected(props.items),
        size: null,
        loading: props.loading,
        angle: 0
      };

      if (props.interactive) {
        this.interactionHandlers = createChartInteraction({
          onStart: this.onStartInteracting,
          onEnd: this.onStopInteracting,
          getSize: () => this.state.size,
          onSelectAngle: this.onSelectAngle,
        });
      }
    }

    onStartInteracting = () => {
      if (this.props.loading) {
        return;
      }
      this.props.onIsInteracting(true);
    };

    onStopInteracting = (angle) => {
      this.props.onIsInteracting(false);
      this.onSelectAngle(angle)
    };

    onSelectAngle = angle => {
      const normalizedAngle = this.normalizeAngle(-(angle + Math.PI / 2));

      const index = this.indexForAngle(normalizedAngle);
      this.setState({ selectedIndex: index }, () => {
        if (this.props.onSelectedItem) {
          this.props.onSelectedItem(this.state.selectedIndex);
        }
      });
    };

    normalizeAngle(angleToNormalize) {
      let angle = angleToNormalize % TAU;

      if (angle < 0) {
        angle += TAU;
      }

      return TAU - angle;
    }

    angleForIndex(index, items = this.state.items) {
      const item = items[index];

      return (item.fromAngle + item.toAngle) / 2;
    }

    indexForAngle(angle) {
      const indexForAngle = findIndex(this.state.items,
        ({ fromAngle, toAngle }) => angle >= fromAngle && angle < toAngle);

      return clamp(indexForAngle, 0, Infinity);
    }

    componentWillReceiveProps(newProps) {
      if (!newProps.loading && this.props.loading) {
        this.setState({ loading: false });
      }

      if (!isEqual(newProps.items, this.props.items)) {
        const items = createChartData(newProps.items);

        const selectedIndex = this.indexForSelected(items);

        this.setState({
          items,
          selectedIndex,
        });
      }
    }

    onLayout = (dimensions) => {

      const ratio = screen.width / screen.height;
      const veryWideScreen = ratio <= 1.6;

      let maxDimensions = dimensions;
      this.veryWideScreen = veryWideScreen;
      if(veryWideScreen) {
        maxDimensions.width = 310;
      }

      if(maxDimensions.width > 490) {
        maxDimensions.width = 490;
      }

      this.setState({ size: maxDimensions.width });
      this.props.onLayout(maxDimensions);
    };

    angleForIndex(index, items = this.state.items) {
      const item = items[index];

      return (item.fromAngle + item.toAngle) / 2;
    }

    indexForSelected(items) {
      const selectedIndex = findIndex(items, (item) => item.selected);
      return clamp(selectedIndex, 0, Infinity);
    }

    donutRotatorRef = (element) => {
      this.donutRotator = element;
    };

    innerRef = (element) => {
      this.innerContainer = element;
    };

    render() {
      const { size, loading, selectedIndex } = this.state;
      const { DonutChartPreloader, onSelectedItem, formatValue, interactive } = this.props;
      const classNameWrapper = this.veryWideScreen ? 'donut-chart__wrapper donut-chart__wrapper--margin-top' : 'donut-chart__wrapper' ;


      return (
        <View style={styles} className="donut-chart" testID="donut-chart">
          <LayoutListener onLayout={this.onLayout}>
            {size > 0 && (
              <View className={classNameWrapper} style={{ maxWidth: this.state.size }}>
                <View style={{ position: 'relative' }}>
                  <View
                    nativeRef={this.donutRotatorRef}
                    pointerEvents="none"
                  >
                    {!loading && (
                      <DonutChartSvg
                        selectedIndex={selectedIndex}
                        onSelectedItem={onSelectedItem}
                        formatValue={formatValue}
                        isTablet={this.props.isTable}
                        items={this.state.items}
                        size={this.state.size}
                      />
                    )}
                  </View>

                  {interactive && (
                    <View
                      testID="donut-chart-interactions"
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        top: 0,
                        backgroundColor: 'transparent',
                      }}
                      {...this.interactionHandlers}
                    />
                  )}

                  {!loading && (
                    <DonutChartInner
                      key="inner"
                      ref={this.innerRef}
                      index={selectedIndex}
                      items={this.props.items}
                      placeholderItem={this.props.placeholderItem}
                      renderSelectedItem={this.props.renderSelectedItem}
                    />
                  )}
                  <DonutChartPreloader size={size} loading={loading} />
                </View>
              </View>
            )}
          </LayoutListener>
        </View>
      );
    }
  };
